import React, { FC } from 'react';
import styles from '../journey-explorer.module.scss';
import { ChurnSignal } from '../../../../models/journey-explorer/journey-explorer';
import { COLORS, formatCompactCurrency, formatNumber } from '../journey-explorer-utils';
import { InteractionInfo, resolveInfo } from '../journey-map-layout';
import { Dot } from './filter-group';

const BAR_WIDTH = 260;

interface ChurnSignalCardProps {
	signal: ChurnSignal;
	lookup: Map<string, InteractionInfo>;
	tone: 'higher' | 'lower';
	minCustomers: number;
	liftScale: number;
	currency: string;
	selected: boolean;
	onSelect: (name: string) => void;
}

const ChurnSignalCard: FC<ChurnSignalCardProps> = ({ signal, lookup, tone, minCustomers, liftScale, currency, selected, onSelect }) => {
	const info = resolveInfo(lookup, signal.name);
	const higher = tone === 'higher';
	const position = (lift: number) => (lift / liftScale) * BAR_WIDTH;

	let note = 'Not clearly different from the average';
	if (signal.clear) {
		note = `Likely between ${signal.lift_low.toFixed(1)}× and ${signal.lift_high.toFixed(1)}×`;
	} else if (signal.customers < minCustomers) {
		note = 'Too few customers to call yet';
	}

	return (
		<button
			type="button"
			className={`${styles.signal_card} ${selected ? styles.signal_card_selected : ''} ${signal.clear ? '' : styles.signal_card_faded}`}
			onClick={() => onSelect(signal.name)}
		>
			<span className={styles.signal_header}>
				<Dot color={info.color} />
				<span className={styles.signal_name}>{info.label}</span>
				<span className={higher ? styles.churn_text : styles.green_text}>{signal.lift.toFixed(1)}×</span>
			</span>
			{higher && (
				<svg width="100%" height="10" viewBox={`0 0 ${BAR_WIDTH} 10`} preserveAspectRatio="none" className={styles.signal_bar}>
					<rect x={0} y={3} width={BAR_WIDTH} height={4} rx={2} fill={COLORS.churnSoft} />
					<rect
						x={position(signal.lift_low)}
						y={1}
						width={Math.max(2, position(signal.lift_high - signal.lift_low))}
						height={8}
						rx={2}
						fill={COLORS.churn}
						opacity={0.35}
					/>
					<rect x={position(signal.lift) - 1} y={0} width={2.5} height={10} fill={COLORS.churn} />
					<rect x={position(1)} y={0} width={1} height={10} fill={COLORS.dim} />
				</svg>
			)}
			<span className={styles.signal_meta}>
				{signal.churn_pct.toFixed(0)}% churned · {formatNumber(signal.customers)}{' '}
				{signal.customers === 1 ? 'customer' : 'customers'}
				{higher && signal.value_at_risk !== null && (
					<>
						{' '}
						· <b>{formatCompactCurrency(signal.value_at_risk, currency)}</b> at risk
					</>
				)}
			</span>
			<span className={styles.signal_note}>{note}</span>
		</button>
	);
};

export default ChurnSignalCard;
